import { logger } from '@/utils/logger';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isTwoFactorVerified: boolean;
  activeOrganizationId: string | null;
  signUp: (email: string, password: string, fullName?: string, organizationId?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  setTwoFactorVerified: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isTwoFactorVerified, setIsTwoFactorVerified] = useState(false); // Start as false
  const activeOrganizationId = session?.user?.app_metadata?.active_organization_id || null;

  useEffect(() => {
    setIsTwoFactorVerified(false);
  }, [user?.id, activeOrganizationId]);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        logger.debug('Auth state change:', event, session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);
        
        // Reset 2FA verification when user signs out or active organization/session changes
        if (!session?.user) {
          setIsTwoFactorVerified(false);
        }
        
        setLoading(false);
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName?: string, organizationId?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName || '',
          organization_id: organizationId || undefined,
          role: 'donor'
        }
      }
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    
    // Don't automatically set 2FA as verified - let the Auth component handle this
    // based on whether the user actually has 2FA enabled or not
  
    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    setIsTwoFactorVerified(false);
    queryClient.removeQueries({ queryKey: ['active-organization'] });
    queryClient.removeQueries({ queryKey: ['currentUserRole'] });
    queryClient.removeQueries({ queryKey: ['usersWithRoles'] });
    return { error };
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl
    });
    return { error };
  };

  const setTwoFactorVerified = (value: boolean) => {
    logger.debug('Setting 2FA verified to:', value);
    setIsTwoFactorVerified(value);
  };

  const value = {
    user,
    session,
    loading,
    isTwoFactorVerified,
    activeOrganizationId,
    signUp,
    signIn,
    signOut,
    resetPassword,
    setTwoFactorVerified
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
